import type { FC, ReactNode } from 'react'
import type { ArticleContent } from '@/types'

import { Fragment } from 'react'
import { ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toFaDigits } from '@/lib/format'

interface FlowDiagramProps {
    diagram: NonNullable<ArticleContent['diagram']>
}

/** Right-to-left process diagram, the on-page stand-in for a draw.io figure. */
export const FlowDiagram: FC<FlowDiagramProps> = ({ diagram }): ReactNode => {
    return (
        <figure className='overflow-hidden rounded-xl border border-border bg-card'>
            <div className='flex items-center justify-between gap-3 border-b border-border bg-secondary/50 px-4 py-2.5'>
                <span className='text-small font-bold text-navy'>{diagram.title}</span>
                <span className='flex gap-1' aria-hidden='true'>
                    <span className='size-2 rounded-full bg-input' />
                    <span className='size-2 rounded-full bg-input' />
                    <span className='size-2 rounded-full bg-input' />
                </span>
            </div>

            <div className='bg-[radial-gradient(circle,#E2E8EF_1px,transparent_1px)] [background-size:16px_16px] p-5 sm:p-6'>
                <ol className='flex flex-col items-stretch gap-2 md:flex-row md:items-center'>
                    {diagram.nodes.map((node, index) => (
                        <Fragment key={node.label}>
                            <li
                                className={cn(
                                    'flex flex-1 flex-col items-center gap-1 rounded-lg border px-3 py-3.5 text-center shadow-card',
                                    index === 0 ? 'border-primary/40 bg-primary-soft' : index === diagram.nodes.length - 1 ? 'border-navy/25 bg-navy text-white' : 'border-border bg-card'
                                )}
                            >
                                <span className={cn('text-caption font-semibold', index === diagram.nodes.length - 1 ? 'text-white/70' : 'text-subtle')}>
                                    {toFaDigits(index + 1)}
                                </span>

                                <span className={cn('text-sm font-bold', index === diagram.nodes.length - 1 ? 'text-white' : 'text-navy')}>
                                    {node.label}
                                </span>

                                <span className={cn('text-caption', index === diagram.nodes.length - 1 ? 'text-white/75' : 'text-muted-foreground')}>
                                    {node.sub}
                                </span>
                            </li>

                            {index < diagram.nodes.length - 1 && (
                                <li aria-hidden='true' className='flex shrink-0 justify-center'>
                                    <ArrowLeft className='size-5 -rotate-90 text-primary md:rotate-0' />
                                </li>
                            )}
                        </Fragment>
                    ))}
                </ol>
            </div>

            <figcaption className='border-t border-border px-4 py-3 text-small text-muted-foreground'>
                {diagram.caption}
            </figcaption>
        </figure>
    )
}
